import React, { useState } from 'react';
import Button from '../../assets/essentials/Button';
import classes from './EventActionModal.module.css';
import PostBody from '../../api/internal/PostBody';
import DateOnlyUTCFormatter from '../../helpers/inputs/DateOnlyUTCFormatter';
import CloseModal from '../../helpers/modal/CloseModal';

type EventActionType = 'approve' | 'deny';

type EventActionModalProps = {
  event: DriverEvent;
  action: EventActionType;
  onSuccess?: () => void;
};

const EventActionModal = ({
  event,
  action,
  onSuccess,
}: EventActionModalProps) => {
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);

  const isDeny = action === 'deny';

  const config = {
    approve: {
      title: 'Approve Event',
      label: 'Approval Notes',
      placeholder: 'Enter any notes about this approval...',
      buttonText: 'Approve Event',
      buttonVariant: 'good' as const,
      successMessage: 'Event Approved ✅',
      endpoint: 'Schedule/ApproveDriverEvent',
      payloadKey: 'approvalNotes',
    },
    deny: {
      title: 'Deny Event',
      label: 'Rejection Reason',
      placeholder: 'Enter the reason for denying this request...',
      buttonText: 'Deny Event',
      buttonVariant: 'bad' as const,
      successMessage: 'Event Denied ❌',
      endpoint: 'Schedule/DenyDriverEvent',
      payloadKey: 'rejectionReason',
    },
  };

  const currentConfig = config[action];

  const onActionSuccess = () => {
    setIsSuccessful(true);
    setIsLoading(false);
    onSuccess?.();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    setIsLoading(true);
    e.preventDefault();

    const payload: any = {
      driverEventId: event.driverEventId,
    };

    if (notes && notes.trim() !== '') {
      payload[currentConfig.payloadKey] = notes;
    }

    PostBody(currentConfig.endpoint, payload)
      .then(res => {
        if (res) {
          onActionSuccess();
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isButtonDisabled = () => {
    if (isLoading || isSuccessful) return true;
    return false;
  };

  return (
    <div
      className={`${classes.container} ${isDeny ? classes.rejectTheme : ''}`}
    >
      <div className={classes.headerContainer}>
        <h1 className={classes.header}>{currentConfig.title}</h1>
        <button
          className={classes.closeButton}
          onClick={CloseModal}
          type="button"
        >
          ×
        </button>
      </div>

      <div className={classes.eventDetailsForm}>
        <h3>Event Details</h3>
        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Driver:</label>
            <p>{event.driverName}</p>
          </div>
          <div className={classes.formGroup}>
            <label>Event Type:</label>
            <p>{event.eventTypeName}</p>
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Start Date:</label>
            <p>{DateOnlyUTCFormatter(event.startDate)}</p>
          </div>
          <div className={classes.formGroup}>
            <label>End Date:</label>
            <p>{DateOnlyUTCFormatter(event.endDate) || 'N/A'}</p>
          </div>
        </div>

        <div className={classes.formGroup}>
          <label>All Day Event:</label>
          <p>{event.isAllDay ? 'Yes' : 'No'}</p>
        </div>

        <div className={classes.formGroup}>
          <label>Notes:</label>
          <p>{event.notes || 'No notes provided'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={classes.form}>
        <div className={classes.formGroup}>
          <label htmlFor="notes">{currentConfig.label}</label>
          <textarea
            name="notes"
            id="notes"
            rows={4}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={currentConfig.placeholder}
          />
        </div>

        <div className={classes.submitButton}>
          <Button
            type="submit"
            variant={currentConfig.buttonVariant}
            disabled={isButtonDisabled()}
            isLoading={isLoading}
          >
            {currentConfig.buttonText}
          </Button>
          {isSuccessful && <p>{currentConfig.successMessage}</p>}
        </div>
      </form>
    </div>
  );
};

export default EventActionModal;
